// The deterministic native-field screen (§4 of the plan, layer 1).
//
// Every candidate custom field is checked against (a) this static registry of
// fields every FMX site captures natively, and (b) the live system-field
// labels from the target entity's own post-options when the caller provides
// them. A hit is reclassified to "FMX already captures this natively — do not
// create", regardless of what the AI said. Example: a "Building" line on a
// work-order form must never become a custom field.
//
// The registry is deliberately conservative: exact normalized-name synonym
// matches only, so a legitimately custom "Building access instructions"
// field is never blocked by the "building" synonym.
//
// Pure module: no React, no Supabase, no fetch.

import { normalizeFieldName } from './dedupe.js';

// mapsTo: short human answer to "where does FMX capture this?".
// names: synonyms in normalized form (see normalizeFieldName).
const NATIVE_REGISTRY = [
  {
    mapsTo: 'the request’s building selection',
    names: ['building', 'building name', 'building number', 'facility', 'facility name', 'campu', 'school', 'site name'],
  },
  {
    mapsTo: 'the request’s location / resource selection',
    names: ['location', 'room', 'room number', 'area', 'resource', 'location of issue', 'location of problem', 'room name'],
  },
  {
    mapsTo: 'the requester’s user record (request contact fields)',
    names: [
      'requester', 'requester name', 'requestor', 'requestor name', 'requested by',
      'submitted by', 'your name', 'contact name', 'name of requester',
      // Forms routinely split the requester across two or three lines.
      // Deliberately NOT a bare "first name"/"last name": these same documents
      // ask "Host First Name" and "Event Lead First Name", which are genuinely
      // custom fields about someone other than the requester.
      'requester first name', 'requester last name', 'requestor first name',
      'requestor last name',
    ],
  },
  {
    mapsTo: 'the requester’s contact info on their user record',
    names: [
      'email', 'email address', 'e mail', 'phone', 'phone number', 'telephone',
      'contact information', 'contact info', 'contact phone', 'contact email',
      'requester email', 'requester email address', 'requestor email', 'requestor email address',
      'your email', 'your email address',
    ],
  },
  {
    mapsTo: 'the request’s short description / name field',
    names: [
      'short description', 'brief description', 'summary', 'title', 'subject',
      // Scheduling forms name the request after the thing being scheduled —
      // "Meeting Name", "Event Name", "Field Trip Name" are all the request's
      // own name, never a custom field.
      'name', 'meeting name', 'event name', 'field trip name', 'overnight trip name',
      'trip name', 'program name', 'activity name', 'name of event', 'name of meeting',
    ],
  },
  {
    mapsTo: 'the request’s detailed description field',
    names: [
      'description', 'detailed description', 'detail', 'work description',
      'description of work', 'description of issue', 'description of problem',
      'work requested', 'work to be performed',
      'meeting description', 'event description', 'trip description',
    ],
  },
  {
    mapsTo: 'the request’s due date',
    names: ['due date', 'date needed', 'needed by', 'need by date', 'date required', 'required by', 'completion date needed', 'deadline'],
  },
  {
    mapsTo: 'the request’s priority level',
    names: ['priority', 'priority level', 'urgency'],
  },
  {
    mapsTo: 'the request type selection',
    names: ['request type', 'type of request', 'type of work', 'work type', 'problem type', 'type of problem', 'type of issue'],
  },
  {
    mapsTo: 'the request’s equipment selection',
    names: ['equipment', 'asset', 'equipment tag', 'asset tag', 'equipment id', 'asset id', 'equipment name'],
  },
  {
    mapsTo: 'request assignment (assigned users)',
    names: ['assigned to', 'assignee', 'assigned technician', 'technician assigned', 'assign to'],
  },
  {
    mapsTo: 'the request’s status (tracked automatically)',
    names: ['statu', 'request statu', 'work order statu'],
  },
  {
    mapsTo: 'the request’s creation timestamp (tracked automatically)',
    names: ['date', 'date submitted', 'submission date', 'date of request', 'request date', 'date created', 'today date'],
  },
  {
    // Scheduling: the occurrence itself carries the date and times, and a
    // schedule request has its own setup/teardown times. Capturing these as
    // custom fields would duplicate the booking the request already IS.
    mapsTo: 'the scheduled occurrence’s date and times',
    names: [
      'date of meeting', 'date of event', 'date of trip', 'event date', 'meeting date',
      'departure date', 'return date',
      'start time', 'end time', 'meeting start time', 'meeting end time',
      'program start time', 'program end time', 'event start time', 'event end time',
      // normalizeFieldName singularizes: "Doors Open Time" → "door open time".
      'door open time', 'departure time', 'return time',
    ],
  },
  {
    mapsTo: 'the schedule request’s setup / teardown time',
    names: ['set up time', 'setup time', 'clean up end time', 'clean up time', 'cleanup time', 'teardown time', 'tear down time'],
  },
  {
    mapsTo: 'the request’s resource / space selection',
    names: [
      // "List the space(s) you are requesting" normalizes with the bracketed
      // plural as its own token: "list the space s you are requesting".
      'list the space s you would like to request', 'list the space s you are requesting',
      'list the space you would like to request', 'list the space you are requesting',
      'space requested', 'space request', 'room requested',
      'which space do you need', 'what space do you need',
    ],
  },
  {
    mapsTo: 'the built-in attachments area on the request',
    names: ['attachment', 'attach file', 'attach photo'],
  },
  {
    mapsTo: 'the work order number (assigned automatically)',
    names: ['work order number', 'work order no', 'wo number', 'request number', 'request id', 'ticket number'],
  },
];

const registryIndex = new Map();
for (const entry of NATIVE_REGISTRY) {
  for (const n of entry.names) registryIndex.set(n, entry);
}

// Screen a candidate name. `liveSystemFieldLabels` is an optional array of
// system-field labels from the TARGET ENTITY's post-options (e.g. the work
// request form's own fields) — not /custom-fields/post-options, which
// describes the custom-field record itself.
// Returns null, or { mapsTo, matchedLabel, source: 'registry' | 'live' }.
export function matchNativeField(candidateName, liveSystemFieldLabels = []) {
  const norm = normalizeFieldName(candidateName);
  if (!norm) return null;
  const reg = registryIndex.get(norm);
  if (reg) return { mapsTo: reg.mapsTo, matchedLabel: candidateName, source: 'registry' };
  for (const label of liveSystemFieldLabels || []) {
    if (normalizeFieldName(label) === norm) {
      return { mapsTo: `the built-in “${label}” field on this form`, matchedLabel: label, source: 'live' };
    }
  }
  return null;
}

// Which import-wizard schema type carries each fieldset's TARGET ENTITY form,
// for fetching live system-field labels via fmxSync.fetchPostOptions. null =
// no entity post-options available through the existing sync (the static
// registry still applies).
export const FIELDSET_ENTITY_SCHEMA = {
  workRequestDetails: 'Work Request',
  workRequestResolution: 'Work Request',
  workRequestAssignment: 'Work Request',
  workRequestResponse: 'Work Request',
  workRequestFinalization: 'Work Request',
  workRequestOnHoldPlacement: 'Work Request',
  workRequestDeletion: 'Work Request',
  workTaskDetails: 'Work Task',
  workTaskInstructionSet: 'Work Task',
  workTaskExecution: 'Work Task',
  workTaskResponse: 'Work Task',
  workTaskFinalization: 'Work Task',
  scheduleRequestDetails: 'Schedule Request',
  scheduleRequestResponse: 'Schedule Request',
  scheduleRequestApproval: 'Schedule Request',
  scheduleRequestDeclination: 'Schedule Request',
  scheduleRequestEstimate: 'Schedule Request',
  scheduleRequestLegacyInvoice: 'Schedule Request',
  scheduleRequestLegacyPayment: 'Schedule Request',
  transportationRequestDetails: 'Transportation Request',
  transportationRequestAssignment: 'Transportation Request',
  transportationRequestResponse: 'Transportation Request',
  transportationRequestApproval: 'Transportation Request',
  transportationRequestDeclination: 'Transportation Request',
  transportationRequestFinalization: 'Transportation Request',
  equipmentItems: 'Equipment',
  equipmentLogs: 'Equipment',
  downtimeEventStart: 'Equipment',
  downtimeEventEnd: 'Equipment',
  inventoryItems: 'Inventory',
  inventoryLogs: 'Inventory',
  buildings: 'Building',
  resources: 'Resource',
  users: 'User',
  utilityProviderBillEntry: 'Utility Provider',
  purchaseOrderDetails: null,
  purchaseOrderPurchase: null,
  purchaseOrderShipmentReceipt: null,
  purchaseOrderLineItems: null,
  invoiceDetails: null,
  invoiceResponse: null,
  invoicePayment: null,
};
